import React from 'react';
import _ from 'lodash';
import {Switch, Route, RouteProps, Redirect} from 'react-router';
import App from './App';
import Login from './components/Login';
import Logout from './components/Logout';
import BillingPage from './pages/BillingPage';
import CreateTrialTeam from './pages/CreateTrialTeam';
import Home from './pages/Home';
import NewProject from './pages/NewProject';
import NewBenchmark from './pages/NewBenchmark';
import Team from './pages/Team';
import GroupPage from './pages/GroupPage';
import SweepPage from './pages/SweepPage';
import ErrorTestPage from './pages/ErrorTestPage';
import OnboardingFlow from './pages/OnboardingFlow';
import ChangePassword from './pages/ChangePassword';
import EntityPage from './pages/EntityPage';
import PublicProjects from './pages/PublicProjects';
import PrivateRoute, {PrivateRouteProps} from './containers/PrivateRoute';
import NoMatch from './components/NoMatch';
import ReportPageEdit from './pages/ReportPageEdit';
import ReportPageView, {
  ReportPageViewPublishedWork,
} from './pages/ReportPageView';
import ReportPageEditOld from './pages/ReportPageEditOld';
import ReportPageViewOld from './pages/ReportPageViewOld';
import NewBenchmarkPage from './pages/Benchmark/NewBenchmarkPage';
import BenchmarkPage from './pages/Benchmark/BenchmarkPage';
import ProjectPage2 from './pages/ProjectPage2';
import QueryDemoPage from './pages/QueryDemoPage';
import RunPage2 from './pages/RunPage2';
import StorageExplorer from './pages/StorageExplorer';
import TrendingRepos from './pages/TrendingRepos';
import TeamAdmin from './pages/TeamAdmin';
import OrganizationAdmin from './pages/OrganizationAdminPage';
import UsersAdmin from './pages/UsersAdminPage';
import LicenseAdmin from './pages/LicenseAdminPage';
import HomePageNew, {HomePageForUser} from './pages/HomePageNew';
import AllProjects from './pages/AllProjects';
import SettingsPage from './pages/SettingsPage';
import AuthorizePage from './pages/AuthorizePage';
import GalleryPage from './pages/GalleryPage';
import TestPage from './pages/TestPage';
import config from './config';
import {Auth} from './setup';
import CreateSweepPage from './pages/CreateSweepPage';
import makeComp from './util/profiler';
import routesCheck, {
  GALLERY_PATH,
  PROFILE_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
  STORAGE_EXPLORER_TRACKED_PATH,
} from './routeData';
import SearchNav from './components/SearchNav';
import StorageNudgeBar from './components/StorageNudgeBar';
import LocalNudgeBar from './components/LocalNudgeBar';
import {ToastContainer} from './components/elements/Toast';

const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;

interface PlainRouteData extends RouteProps {
  RouteComponent: typeof Route;
}

interface RouteWithLayoutData extends RouteWithLayoutProps {
  RouteComponent: typeof RouteWithLayout;
}

interface PrivateRouteData extends PrivateRouteProps {
  RouteComponent: typeof PrivateRoute;
}

interface RedirectData {
  path: string | string[];
  exact?: true;
  redirect: string;
}

type RouteData = (
  | PlainRouteData
  | RouteWithLayoutData
  | PrivateRouteData
  | RedirectData
) & {
  condition?: boolean;
};

export interface RouteWithLayoutProps {
  component: React.ComponentType<any>;
  error?: any;
  auth?: Auth;
  additionalProps?: {[prop: string]: any};
  [prop: string]: any;
}

// adds the Nav and Footer before and after the route component
export const RouteWithLayout: React.FC<RouteWithLayoutProps> = makeComp(
  ({component: Component, error, auth, additionalProps = {}, ...rest}) => {
    return (
      <Route
        {...rest}
        render={routeProps => {
          return (
            <React.Fragment>
              <SearchNav {...routeProps} />
              <StorageNudgeBar />
              {rest.hasCurrentViewer && <LocalNudgeBar />}
              <div className="main">
                <ToastContainer />
                <Component
                  error={error}
                  auth={auth}
                  {...routeProps}
                  {...additionalProps}
                />
              </div>
            </React.Fragment>
          );
        }}
      />
    );
  },
  {id: 'RouteWithLayout'}
);

export function makeRoutes(auth?: Auth): RouteData[] {
  const routes = [
    {
      RouteComponent: Route,
      path: '/__test_page',
      render: () => <TestPage />,
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/',
      component: Home,
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/login',
      component: Login,
      auth,
    },
    {
      RouteComponent: Route,
      path: '/logout',
      render: () => <Logout auth={auth} />,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/trending',
      component: TrendingRepos,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/home',
      component: HomePageNew,
      routeCache: 'HomePage',
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/home/projects',
      component: AllProjects,
      routeCache: 'AllProjectsPage',
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/projects',
      component: PublicProjects,
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: GALLERY_PATH,
      component: GalleryPage,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/__test/simple_error',
      component: ErrorTestPage,
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/users/:userName',
      component: HomePageForUser,
      routeCache: 'UserPage',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/signup',
      component: OnboardingFlow,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/change-password',
      component: ChangePassword,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/authorize',
      component: AuthorizePage,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/settings',
      component: SettingsPage,
      routeCache: 'SettingsPage',
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: ['/billing', '/subscriptions'],
      component: BillingPage,
      routeCache: 'BillingPage',
      exact: true,
      condition: !isOnPrem,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/team',
      component: TeamAdmin,
      routeCache: 'TeamAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/organization',
      component: OrganizationAdmin,
      routeCache: 'OrganizationAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/users',
      component: UsersAdmin,
      routeCache: 'UsersAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/license',
      component: LicenseAdmin,
      routeCache: 'LicenseAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/create-team',
      component: CreateTrialTeam,
      routeCache: 'CreateTrialTeam',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/teams/:entityName',
      component: Team,
      routeCache: 'TeamPage',
    },
    {
      RouteComponent: RouteWithLayout,
      path: [
        '/storage/:entityName',
        STORAGE_EXPLORER_TRACKED_PATH,
        '/storage/:entityName/:filePath*',
      ],
      component: StorageExplorer,
      routeCache: 'StorageExplorer',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/new-project',
      component: NewProject,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/new-benchmark',
      component: NewBenchmark,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityname/benchmark/new',
      component: NewBenchmarkPage,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/:entityName/projects',
      component: EntityPage,
      routeCache: 'EntityPage',
      exact: true,
      additionalProps: {tab: 'projects'},
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/:entityName/likes',
      component: EntityPage,
      routeCache: 'EntityPage',
      exact: true,
      additionalProps: {tab: 'likes'},
    },
    {
      RouteComponent: PrivateRoute,
      path: [
        '/:entityName/:projectName/runs/:runName',
        '/:entityName/:projectName/runs/:runName/:tab(files|code)/:filePath*',
        '/:entityName/:projectName/runs/:runName/:tab',
      ],
      component: RunPage2,
      routeCache: 'RunPage2',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: [
        '/:entityName/:projectName/groups/:groupName',
        '/:entityName/:projectName/groups/:groupName/:tab',
      ],
      component: GroupPage,
      routeCache: 'GroupPage',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/create-sweep',
      component: CreateSweepPage,
      routeCache: 'CreateSweepPage',
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: [
        '/:entityName/:projectName/sweeps/:sweepName',
        '/:entityName/:projectName/sweeps/:sweepName/:tab',
      ],
      component: SweepPage,
      routeCache: 'SweepPage',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: [
        '/:entityName/:projectName/benchmark',
        '/:entityName/:projectName/benchmark/:tab/:threadID?',
      ],
      component: BenchmarkPage,
      routeCache: 'BenchmarkPage',
      exact: true,
      allowAnonymous: true,
    },
    // {
    //   RouteComponent: PrivateRoute,
    //   path: '/:entityName/published-work',
    //   component: PublishedWorkPage,
    //   routeCache: 'PublishedWorkPage',
    //   exact: true,
    //   allowAnonymous: true,
    // },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/reports',
      component: ReportPageEditOld,
      routeCache: 'ReportPageEditOld',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/published',
      component: ReportPageViewOld,
      routeCache: 'ReportPageViewOld',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/reports/:reportNameAndID/edit',
      component: ReportPageEdit,
      routeCache: 'ReportPageEdit',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: REPORT_PAGE_VIEW_PATH,
      component: ReportPageView,
      routeCache: 'ReportPageView',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
      component: ReportPageViewPublishedWork,
      routeCache: 'ReportPageViewPublishedWork',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/:entityName/:projectName/querydemo',
      component: QueryDemoPage,
      routeCache: 'ProjectPage2',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: [
        '/:entityName/:projectName',
        '/:entityName/:projectName/:tab(overview|workspace|table|reportlist|sweeps|run-queue|artifacts)',
        '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName(new)',
        '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName',
        '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCommitHash(new)',
        '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName/:artifactCommitHash/:artifactTab(files)/:filePath*',
        '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName/:artifactCommitHash/:artifactTab?',
        '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName',
      ],
      component: ProjectPage2,
      routeCache: 'ProjectPage2',
      exact: true,
      allowAnonymous: true,
    },
    {
      path: '/:entityName/:projectName',
      redirect: '/:entityName/:projectName',
    },
    {
      RouteComponent: RouteWithLayout,
      path: PROFILE_PATH,
      component: EntityPage,
      routeCache: 'EntityPage',
      exact: true,
      allowAnonymous: true,
    },
  ];

  // HAX: These checks ensure that the route mapping above is in sync with routeData.ts.
  // routeData.ts is shared between the main app and the host function.
  if (routes.length !== routesCheck.length) {
    throw new Error(
      `mismatched routes length: ${routes.length}, ${routesCheck.length}`
    );
  }
  for (let i = 0; i < routes.length; i++) {
    const r = routes[i];
    const rc = routesCheck[i];
    if (
      !_.isEqual(r.path, rc.path) ||
      r.redirect !== rc.redirect ||
      r.exact !== rc.exact
    ) {
      throw new Error(`mismatched routes: ${r.path}, ${rc.path}`);
    }
  }

  return routes;
}

const Routes: React.FC<{auth: Auth}> = makeComp(
  ({auth}) => {
    return (
      <App>
        <Switch>
          {makeRoutes(auth)
            .filter(r => r.condition !== false)
            .map((r, i) => {
              if ('redirect' in r) {
                return <Redirect key={i} from={r.path} to={r.redirect} />;
              }
              const {RouteComponent, condition, ...rest} = r;
              const RC = RouteComponent as any;
              return <RC key={i} {...rest} />;
            })}
          <RouteWithLayout component={NoMatch} />
        </Switch>
      </App>
    );
  },
  {id: 'Routes'}
);

export default Routes;
